/* Natora treat box quiz
 * ---------------------
 * DO NOT PUT USER-FACING TEXT IN THIS FILE.
 *
 * Every string lives in locales/en.default.json under `natora_quiz`. The
 * section resolves them with the `t` filter and hands the ones we need over,
 * already translated, in the [data-quiz-i18n] JSON blob. We look them up by key
 * and substitute %tokens%. Adding a sentence here would ship it untranslated in
 * every language.
 */
(function () {
  'use strict';

  var TOKEN = /%([a-z]+)%/g;

  /* Klaviyo's client endpoint takes the PUBLIC company id only — never a
     private key, which would be readable by anyone in the theme asset. Pinned
     revision: Klaviyo versions its API by date, and an unpinned request would
     drift onto a future breaking change without warning. */
  var KLAVIYO_URL = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2026-07-15';

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* Shopify renders <html lang="en">, and bare "en" formats dates the US way
     ("Aug 17, 2026"). The section passes language+market ("en-NL", "de-DE") so
     a European store gets "17 Aug 2026". Intl takes a list and uses the first
     it supports, so we hand it every candidate in preference order. */
  function localeList(root) {
    var out = [];
    [root.getAttribute('data-date-locale'), document.documentElement.getAttribute('lang')].forEach(
      function (tag) {
        if (!tag || !tag.trim()) return;
        try {
          Intl.getCanonicalLocales(tag.trim());
          out.push(tag.trim());
        } catch (e) {
          /* malformed tag — skip it rather than let Intl throw later */
        }
      }
    );
    out.push('en-GB');
    return out;
  }

  function addMonths(date, months) {
    var day = date.getDate();
    var d = new Date(date.getTime());
    d.setDate(1);
    d.setMonth(d.getMonth() + months);
    /* Clamp so 31 Jan + 1 month is the last day of Feb, not the 3rd of Mar. */
    var lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, lastDay));
    return d;
  }

  function formatDate(locales, months) {
    return new Intl.DateTimeFormat(locales, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(addMonths(new Date(), months));
  }

  function readStrings(root) {
    var node = root.querySelector('[data-quiz-i18n]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return {};
    }
  }

  function init(root) {
    var autoAdvance = root.getAttribute('data-auto-advance') === 'true';
    var totalSteps = parseInt(root.getAttribute('data-total-steps'), 10) || 7;
    var locales = localeList(root);
    var strings = readStrings(root);

    var state = {
      step: 0,
      name: '',
      treatType: null,
      allergies: [],
      other: '',
      email: '',
      boxSize: null
    };

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-step]'));
    var segments = Array.prototype.slice.call(root.querySelectorAll('[data-seg]'));
    var modal = root.querySelector('[data-quiz-modal]');
    var moveFocus = false;
    var returnFocusTo = null;

    /* ---------- derived values ---------- */

    function dogName() {
      return state.name.trim() || strings['general.fallback_name'] || '';
    }

    function labelFor(group, value) {
      var card = root.querySelector('[data-choice="' + group + '"][data-value="' + value + '"]');
      var label = card && card.querySelector('[data-label]');
      return label ? label.textContent.trim() : '';
    }

    /* Box type + size resolve to one of the product's variants. The ids are
       rendered onto the box-size cards by Liquid, keyed by the Shopify option
       value ("Small Treats"), which is why the treat cards carry
       data-option-value alongside their own key ("small_treats"). Nothing is
       hardcoded here — repoint the product setting and this still works. */
    function variantId() {
      if (!state.treatType || !state.boxSize) return '';
      var typeCard = root.querySelector('[data-choice="treatType"][data-value="' + state.treatType + '"]');
      var sizeCard = root.querySelector('[data-choice="boxSize"][data-value="' + state.boxSize + '"]');
      if (!typeCard || !sizeCard) return '';
      var optionValue = typeCard.getAttribute('data-option-value');
      if (!optionValue) return '';
      var variant = sizeCard.querySelector('[data-variant][data-variant-type="' + optionValue + '"]');
      return variant ? variant.getAttribute('data-variant-id') : '';
    }

    /* "Other" is a catch-all: its free text is what matters, not the word. */
    function allergyText() {
      var parts = state.allergies
        .filter(function (key) {
          return key !== 'other';
        })
        .map(function (key) {
          var pill = root.querySelector('[data-allergy="' + key + '"] [data-label]');
          return pill ? pill.textContent.trim() : '';
        })
        .filter(Boolean);
      var other = state.other.trim();
      if (other) parts.push(other);
      return parts.join(', ');
    }

    /* An empty property still shows as a blank row on the order, so drop it —
       disabled inputs aren't submitted. */
    function setProperty(input, value) {
      if (!input) return;
      input.value = value;
      input.disabled = !value;
    }

    /* ---------- rendering ---------- */

    function valuesFor(el) {
      var vals = {
        name: dogName(),
        /* %box% is whichever box type they chose, not a recommendation. */
        box: state.treatType ? strings['treats.' + state.treatType + '_label'] || '' : '',
        current: String(state.step + 1),
        total: String(totalSteps)
      };
      var plan = el && el.closest('[data-months]');
      if (plan) {
        vals.date = formatDate(locales, parseInt(plan.getAttribute('data-months'), 10) || 1);
      }
      return vals;
    }

    /* Escape the whole string first, then splice in already-escaped values, so
       a dog called <script> stays inert. */
    function fill(el, key) {
      var tpl = strings[key];
      if (typeof tpl !== 'string') return;
      var vals = valuesFor(el);
      el.innerHTML = esc(tpl).replace(TOKEN, function (match, token) {
        if (!Object.prototype.hasOwnProperty.call(vals, token)) return match;
        /* The box name is the one token the design renders as emphasis. */
        if (token === 'box') return '<strong class="nq-em">' + esc(vals[token]) + '</strong>';
        return esc(vals[token]);
      });
    }

    function highlights(group) {
      /* The design only persists a selected state on the box-size cards, because
         age and size auto-advance. With auto-advance off you need to see what
         you picked, so highlight everything. */
      return group === 'boxSize' || !autoAdvance;
    }

    function render() {
      steps.forEach(function (step) {
        step.hidden = parseInt(step.getAttribute('data-step'), 10) !== state.step;
      });

      segments.forEach(function (seg, i) {
        seg.classList.toggle('is-done', i <= state.step);
      });

      Array.prototype.forEach.call(root.querySelectorAll('[data-choice]'), function (card) {
        var group = card.getAttribute('data-choice');
        var selected = highlights(group) && state[group] === card.getAttribute('data-value');
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      /* Summary chips echo the label straight off the card that was chosen, so
         they reuse its translation instead of needing a key of their own. */
      Array.prototype.forEach.call(root.querySelectorAll('[data-chip]'), function (chip) {
        var group = chip.getAttribute('data-chip');
        var value = state[group];
        var out = chip.querySelector('[data-chip-out]');
        chip.hidden = !value;
        if (value && out) out.textContent = labelFor(group, value);
      });

      Array.prototype.forEach.call(root.querySelectorAll('[data-allergy]'), function (pill) {
        var on = state.allergies.indexOf(pill.getAttribute('data-allergy')) !== -1;
        pill.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      var otherBox = root.querySelector('[data-other-box]');
      if (otherBox) otherBox.hidden = state.allergies.indexOf('other') === -1;

      var cta = root.querySelector('[data-cta-out]');
      if (cta) {
        fill(cta, state.allergies.length ? 'allergies.cta_chosen' : 'allergies.cta_empty');
      }

      Array.prototype.forEach.call(root.querySelectorAll('[data-i18n]'), function (el) {
        fill(el, el.getAttribute('data-i18n'));
      });

      /* Every plan card posts the same variant and answers; only the selling
         plan differs. Guard the button rather than post an empty id, which
         Shopify would reject with a 404. */
      var id = variantId();
      var allergies = allergyText();
      Array.prototype.forEach.call(root.querySelectorAll('[data-cart-form]'), function (form) {
        var variantInput = form.querySelector('[data-variant-input]');
        if (variantInput) variantInput.value = id;
        setProperty(form.querySelector('[data-prop="name"]'), state.name.trim());
        setProperty(form.querySelector('[data-prop="allergies"]'), allergies);
        var cta = form.querySelector('[data-cta]');
        if (cta) cta.disabled = !id;
      });

      if (moveFocus) {
        moveFocus = false;
        var active = steps.filter(function (s) {
          return !s.hidden;
        })[0];
        var heading = active && active.querySelector('.nq-title');
        if (heading) heading.focus({ preventScroll: true });
      }
    }

    function go(step) {
      var next = Math.max(0, Math.min(totalSteps - 1, step));
      if (next === state.step) return;
      state.step = next;
      moveFocus = true;
      render();
    }

    /* ---------- modal ---------- */

    /* `opener` is passed explicitly rather than read from document.activeElement:
       Safari does not focus a button on click, so activeElement would be the
       body and focus would return to the top of the page on close. */
    function openQuiz(step, opener) {
      if (!modal) return;
      returnFocusTo = opener || document.activeElement;
      /* Belt and braces: the dialog alone does not reliably stop the page behind
         it from scrolling in every browser. */
      document.documentElement.style.overflow = 'hidden';
      if (typeof modal.showModal === 'function') {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }
      state.step = Math.max(0, Math.min(totalSteps - 1, step || 0));
      moveFocus = true;
      render();
    }

    function closeQuiz() {
      if (!modal) return;
      if (typeof modal.close === 'function') {
        modal.close();
      } else {
        modal.removeAttribute('open');
        releaseModal();
      }
    }

    function releaseModal() {
      document.documentElement.style.overflow = '';
      /* Esc and the close button both land here, so focus always goes back to
         whatever opened the quiz rather than the top of the page. */
      if (returnFocusTo && typeof returnFocusTo.focus === 'function') {
        returnFocusTo.focus({ preventScroll: true });
      }
      returnFocusTo = null;
    }

    if (modal) {
      /* Fires for the close button, Esc, and form submission alike. */
      modal.addEventListener('close', releaseModal);
    }

    function advance() {
      go(state.step + 1);
    }

    function validEmail(value) {
      return /.+@.+\..+/.test(value);
    }

    /* Fire-and-forget on purpose: the quiz advances whether or not this lands.
       Klaviyo being slow or down must never stop someone buying, and a marketing
       signup is not worth an error message in front of a customer. Guarded by
       the last email sent so going Back and Forward doesn't re-post. */
    var sentEmail = '';
    function subscribeEmail() {
      var company = root.getAttribute('data-klaviyo-company');
      var list = root.getAttribute('data-klaviyo-list');
      var email = state.email.trim();
      if (!company || !list || !validEmail(email) || sentEmail === email) return;
      sentEmail = email;

      /* Property keys are Klaviyo's schema, not copy — they stay in English on
         every market so one flow can read them. Allergies deliberately are not
         sent: they ride on the order instead, where fulfilment can act on them.

         NOT $source. That is reserved: Klaviyo overwrites it on this endpoint
         with -9 ("API (subscribe)"), so anything we put there is discarded.
         `Quiz` is a custom property and cannot be clobbered. */
      var properties = { Quiz: 'yes' };
      /* Language, not market: a French speaker in Belgium is "fr". */
      var locale = root.getAttribute('data-locale');
      if (locale) properties.Language = locale;
      var dogName = state.name.trim();
      if (dogName) properties.dogs_name = dogName;

      var profile = { type: 'profile', attributes: { email: email } };
      if (Object.keys(properties).length) profile.attributes.properties = properties;

      var payload = {
        data: {
          type: 'subscription',
          attributes: { profile: { data: profile } },
          relationships: { list: { data: { type: 'list', id: list } } }
        }
      };

      try {
        window
          .fetch(KLAVIYO_URL + '?company_id=' + encodeURIComponent(company), {
            method: 'POST',
            headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
            body: JSON.stringify(payload),
            /* survives the page unloading if they reach checkout quickly */
            keepalive: true
          })
          .catch(function () {
            /* swallowed deliberately — see above */
          });
      } catch (e) {
        /* no fetch, or blocked: still not the customer's problem */
      }
    }

    /* ---------- events ---------- */

    /* The hero owns step 1. Submitting it prefills the name and jumps straight
       to the treat type, but the quiz keeps its own name step so Back still
       lets them correct it. */
    var heroForm = root.querySelector('[data-hero-form]');
    if (heroForm) {
      heroForm.addEventListener('submit', function (event) {
        event.preventDefault();
        var field = heroForm.querySelector('[data-hero-name]');
        var value = field ? field.value.trim() : '';
        if (!value) {
          if (field) field.focus();
          return;
        }
        state.name = value;
        var nameStepField = root.querySelector('[data-input="name"]');
        if (nameStepField) nameStepField.value = value;
        openQuiz(1, field);
      });
    }

    /* Any button on the page can open the quiz — a header CTA, a banner. Skips
       the name step only if we already have a name. */
    document.addEventListener('click', function (event) {
      if (!event.target.closest) return;
      var trigger = event.target.closest('[data-natora-quiz-open]');
      if (!trigger) return;
      event.preventDefault();
      openQuiz(state.name.trim() ? 1 : 0, trigger);
    });

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-quiz-close]')) {
        closeQuiz();
        return;
      }

      var choice = event.target.closest('[data-choice]');
      if (choice && root.contains(choice)) {
        state[choice.getAttribute('data-choice')] = choice.getAttribute('data-value');
        render();
        if (autoAdvance) setTimeout(advance, 220);
        return;
      }

      var allergy = event.target.closest('[data-allergy]');
      if (allergy && root.contains(allergy)) {
        var key = allergy.getAttribute('data-allergy');
        var at = state.allergies.indexOf(key);
        if (at === -1) {
          state.allergies.push(key);
        } else {
          state.allergies.splice(at, 1);
        }
        render();
        return;
      }

      var next = event.target.closest('[data-next]');
      if (next && root.contains(next)) {
        var gate = next.getAttribute('data-next');
        if (gate === 'name' && !state.name.trim()) return;
        if (gate === 'email') {
          if (!validEmail(state.email)) return;
          subscribeEmail();
        }
        /* Only rendered when auto-advance is off. */
        if ((gate === 'treatType' || gate === 'boxSize') && !state[gate]) return;
        advance();
        return;
      }

      if (event.target.closest('[data-skip]')) {
        advance();
        return;
      }

      if (event.target.closest('[data-back]')) {
        go(state.step - 1);
      }
    });

    root.addEventListener('input', function (event) {
      var field = event.target.closest('[data-input]');
      if (!field) return;
      var name = field.getAttribute('data-input');
      state[name] = field.value;
      /* Only the name feeds interpolated copy; skip the re-render otherwise. */
      if (name === 'name') render();
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var field = event.target.closest('[data-input]');
      if (!field) return;
      var name = field.getAttribute('data-input');
      if (name === 'name' && state.name.trim()) {
        event.preventDefault();
        advance();
      }
      if (name === 'email' && validEmail(state.email)) {
        event.preventDefault();
        subscribeEmail();
        advance();
      }
    });

    render();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-natora-quiz]'), function (root) {
      if (root.hasAttribute('data-quiz-ready')) return;
      root.setAttribute('data-quiz-ready', '');
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify theme editor re-renders sections on setting changes. */
  document.addEventListener('shopify:section:load', boot);
})();
